import { useQuery } from '@tanstack/react-query';
import { apiGet } from '../../shared/api/client';
import { useValidatedBabyId } from '../../shared/hooks/useValidatedBabyId';
import { useEntitlement } from '../subscription/useEntitlement';

/**
 * Shared day-schedule query (Sleep Suite, v1.2.0) — one cache entry feeds
 * SleepSchedule, WakeWindowIndicator and the SleepSuiteServices driver
 * (notifications + Live Activity + widget data).
 *
 * GET /babies/{babyId}/predictions/schedule?tz=<IANA>
 * Response: { napsPerDay, source, anchor, naps[], bedtime, wakeWindowMinutes[],
 *             avgWakeWindowMinutes, avgNapMinutes, confidence, basedOnSessions,
 *             ageMonths,
 *             usualWakeTime ("HH:MM" local in the requested tz | null),
 *             avgNightMinutes (number | null), nightSamples }   ← v1.4.3
 * A nap/bedtime that is the open sleep carries `inProgress: true` (v1.3.0).
 * The night fields feed expectedWake.js (home / Sleep heroes, Live Activity).
 *
 * Premium-gated behind `pattern_detection` (7-day preview counts as entitled).
 * Every sleep mutation invalidates ['sleep-schedule'] at its call site
 * (useOpenSleep's actions, PastSleepForm, SleepHistory, QuickLogSheet); app
 * resume invalidates it from the SleepSuiteServices driver.
 */
export function useSleepSchedule() {
  const { babies, selectedBabyId: babyId } = useValidatedBabyId();
  const { hasFeature, isLoading: subLoading } = useEntitlement();
  const entitled = hasFeature('pattern_detection');

  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/London';

  const query = useQuery({
    queryKey: ['sleep-schedule', babyId],
    queryFn: () => apiGet(`/babies/${babyId}/predictions/schedule`, { tz }),
    enabled: !!babyId && !subLoading && entitled,
    staleTime: 5 * 60 * 1000,
    // Global default is false — the schedule specifically must refresh when
    // the user comes back to the app so countdowns and predictions are live.
    refetchOnWindowFocus: true,
  });

  const baby = babies.find((b) => b.babyId === babyId) || null;

  return { ...query, babyId, baby, entitled, subLoading };
}

/**
 * Next upcoming schedule event — first remaining predicted nap, else bedtime.
 * Returns { label, time (ISO), isBedtime } or null when the day is done.
 */
export function getNextScheduleEvent(schedule, now = new Date()) {
  if (!schedule) return null;
  const events = [];
  (schedule.naps || []).forEach((nap) => {
    if (!nap.taken && nap.start) {
      events.push({ label: nap.label || `Nap ${nap.index}`, time: nap.start, isBedtime: false });
    }
  });
  if (schedule.bedtime?.start && !schedule.bedtime.taken) {
    events.push({ label: 'Bedtime', time: schedule.bedtime.start, isBedtime: true });
  }
  return (
    events
      .filter((e) => new Date(e.time) > now)
      .sort((a, b) => new Date(a.time) - new Date(b.time))[0] || null
  );
}
